import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "regen-header" }
const _hoisted_3 = { class: "regen-header-inner" }
const _hoisted_4 = { class: "regen-header-icon" }
const _hoisted_5 = { class: "regen-header-text" }
const _hoisted_6 = { class: "regen-title" }
const _hoisted_7 = { class: "regen-subtitle" }
const _hoisted_8 = { class: "regen-body" }
const _hoisted_9 = {
  key: 2,
  class: "regen-notice-text"
}
const _hoisted_10 = {
  key: 3,
  class: "regen-notice-text"
}
const _hoisted_11 = { class: "regen-notice-section" }
const _hoisted_12 = {
  key: 0,
  class: "regen-auto-card"
}
const _hoisted_13 = { class: "regen-auto-card-header" }
const _hoisted_14 = {
  key: 1,
  class: "regen-upload-group"
}
const _hoisted_15 = { class: "regen-dropzone-icon-wrap" }
const _hoisted_16 = {
  key: 2,
  class: "regen-files"
}
const _hoisted_17 = { class: "regen-files-header" }
const _hoisted_18 = { class: "regen-files-count" }
const _hoisted_19 = { class: "regen-files-list" }
const _hoisted_20 = { class: "regen-file-info" }
const _hoisted_21 = ["title"]
const _hoisted_22 = { class: "regen-file-size" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "regen-model-group" }
const _hoisted_25 = { class: "regen-label" }
const _hoisted_26 = { class: "regen-model-select-wrap" }
const _hoisted_27 = {
  key: 3,
  class: "regen-error"
}
const _hoisted_28 = { class: "regen-footer" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = ["disabled"]

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import {
  RefreshCw,
  Info,
  UploadCloud,
  FileText,
  X,
  Sparkles,
  AlertCircle,
  ChevronDown,
} from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import { regenerateReportSection } from "@/services/reportsService";
import type { ReportRecord } from "@/types/reports";


export default /*@__PURE__*/_defineComponent({
  __name: 'SectionRegenerateDialog',
  props: {
    open: { type: Boolean },
    reportId: {},
    sectionKey: {},
    sectionLabel: {},
    isAutoDerived: { type: Boolean, default: false },
    currentAiModel: {}
  },
  emits: ["update:open", "regenerated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const isAutoDerived = computed(() => {
  return (
    Boolean(props.isAutoDerived) ||
    ['executivesnapshot', 'executive', 'executive_snapshot'].includes(
      (props.sectionKey || '').toLowerCase().replace(/[-_]/g, '')
    )
  );
});

const emit = __emit;

const toast = useToast();

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit("update:open", val),
});

// Close on Escape key without affecting parent dialogs
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && isOpen.value) {
    e.stopPropagation();
    e.stopImmediatePropagation();
    isOpen.value = false;
  }
}
onMounted(() => document.addEventListener("keydown", onKeydown, true));
onUnmounted(() => document.removeEventListener("keydown", onKeydown, true));

// Reset files and ensure pointer events are unlocked when dialog opens
watch(
  () => props.open,
  (val) => {
    if (val) {
      selectedFiles.value = [];
      errorMessage.value = null;
      if (typeof document !== "undefined") {
        document.body.style.pointerEvents = "auto";
      }
    }
  }
);

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFiles = ref<File[]>([]);
const isDragging = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref<string | null>(null);
const aiModel = ref<string>(props.currentAiModel || "gpt-4o-mini");

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    addFiles(Array.from(target.files));
    target.value = "";
  }
}

function handleDrop(event: DragEvent) {
  isDragging.value = false;
  if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
    addFiles(Array.from(event.dataTransfer.files));
  }
}

function addFiles(files: File[]) {
  errorMessage.value = null;
  selectedFiles.value = [...selectedFiles.value, ...files];
}

function removeFile(index: number) {
  selectedFiles.value.splice(index, 1);
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

async function handleRegenerate() {
  if (!props.reportId) {
    errorMessage.value = "No active report found to regenerate.";
    return;
  }
  if (!isAutoDerived.value && selectedFiles.value.length === 0) {
    errorMessage.value = "Please select at least one document to upload.";
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;

  try {
    const updatedReport = await regenerateReportSection(
      props.reportId,
      props.sectionKey,
      selectedFiles.value,
      { aiModel: aiModel.value }
    );
    toast.success(`"${props.sectionLabel}" regenerated successfully!`);
    emit("regenerated", updatedReport);
    isOpen.value = false;
    selectedFiles.value = [];
  } catch (err: any) {
    console.error("Failed regenerating report section:", err);
    errorMessage.value =
      err?.response?.data?.message ||
      err?.message ||
      "An unexpected error occurred while regenerating this section.";
  } finally {
    isSubmitting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "regen-modal-root",
          "data-dismissable-layer": "",
          onPointerdown: _cache[11] || (_cache[11] = _withModifiers(() => {}, ["stop"])),
          onMousedown: _cache[12] || (_cache[12] = _withModifiers(() => {}, ["stop"])),
          onClick: _cache[13] || (_cache[13] = _withModifiers(() => {}, ["stop"]))
        }, [
          _createVNode(_Transition, {
            name: "regen-fade",
            appear: ""
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                class: "regen-backdrop",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (isOpen.value = false))
              })
            ]),
            _: 1
          }),
          _createVNode(_Transition, {
            name: "regen-pop",
            appear: ""
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                class: "regen-shell",
                role: "dialog",
                "aria-modal": "true",
                "aria-label": 'Regenerate ' + __props.sectionLabel,
                onPointerdown: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["stop"])),
                onMousedown: _cache[9] || (_cache[9] = _withModifiers(() => {}, ["stop"])),
                onClick: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      _createVNode(_unref(RefreshCw), { class: "regen-header-icon-svg" })
                    ]),
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("p", _hoisted_6, "Regenerate " + _toDisplayString(__props.sectionLabel), 1),
                      _createElementVNode("p", _hoisted_7, _toDisplayString(isAutoDerived.value ? 'Auto-recalculate metrics, statuses, and insights from active sections' : "Upload a new document to refresh this section's data"), 1)
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "regen-close",
                      "aria-label": "Close",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (isOpen.value = false))
                    }, [
                      _createVNode(_unref(X), { class: "regen-close-icon" })
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["regen-notice", { 'regen-notice--auto': isAutoDerived.value }])
                  }, [
                    (isAutoDerived.value)
                      ? (_openBlock(), _createBlock(_unref(Sparkles), {
                          key: 0,
                          class: "regen-notice-icon",
                          style: {"color":"#2563eb"}
                        }))
                      : (_openBlock(), _createBlock(_unref(Info), {
                          key: 1,
                          class: "regen-notice-icon"
                        })),
                    (isAutoDerived.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_9, [...(_cache[14] || (_cache[14] = [
                          _createElementVNode("strong", null, "Cross-Channel Auto-Derived:", -1),
                          _createTextVNode(" Executive Snapshot automatically derives KPIs, operational delivery statuses, and strategic insights directly from your active marketing sections (Social Media, Leads, SEO, Chatbot, Blogs, Paid Ads, AI Podcast). ", -1),
                          _createElementVNode("br", null, null, -1),
                          _createElementVNode("br", null, null, -1),
                          _createTextVNode(" No file upload is required. Regenerating will recalculate headline metrics, refresh comparison chart data, update operational delivery checklists, and synthesize fresh insights from all available section data. ", -1)
                        ]))]))
                      : (_openBlock(), _createElementBlock("p", _hoisted_10, [
                          _cache[15] || (_cache[15] = _createElementVNode("strong", null, "Section Replacement:", -1)),
                          _cache[16] || (_cache[16] = _createTextVNode(" This will replace the raw data and metrics for ", -1)),
                          _createElementVNode("strong", _hoisted_11, _toDisplayString(__props.sectionLabel), 1),
                          _cache[17] || (_cache[17] = _createTextVNode(" only. All other sections and existing custom text remain unaffected. ", -1))
                        ]))
                  ], 2),
                  (isAutoDerived.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          _createVNode(_unref(RefreshCw), { class: "regen-auto-card-icon" }),
                          _cache[18] || (_cache[18] = _createElementVNode("span", null, "What will be recalculated:", -1))
                        ]),
                        _cache[19] || (_cache[19] = _createElementVNode("ul", { class: "regen-auto-list" }, [
                          _createElementVNode("li", null, [
                            _createElementVNode("strong", null, "Headline KPIs:"),
                            _createTextVNode(" Top metric per active channel (Social, Leads, SEO, Blogs, Ads, Podcast)")
                          ]),
                          _createElementVNode("li", null, [
                            _createElementVNode("strong", null, "Comparative Bar Chart:"),
                            _createTextVNode(" Period-over-period multi-service comparison data")
                          ]),
                          _createElementVNode("li", null, [
                            _createElementVNode("strong", null, "Current Status:"),
                            _createTextVNode(" Operational delivery checklist from active services")
                          ]),
                          _createElementVNode("li", null, [
                            _createElementVNode("strong", null, "Insights & Recommendations:"),
                            _createTextVNode(" Fresh strategic narrative synthesized from live channel data")
                          ])
                        ], -1))
                      ]))
                    : _createCommentVNode("", true),
                  (!isAutoDerived.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                        _cache[22] || (_cache[22] = _createElementVNode("label", { class: "regen-label" }, [
                          _createTextVNode(" Upload Document(s) "),
                          _createElementVNode("span", { class: "regen-required" }, "*")
                        ], -1)),
                        _createElementVNode("label", {
                          class: _normalizeClass(["regen-dropzone", { 'regen-dropzone--active': isDragging.value }]),
                          onDragover: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (isDragging.value = true), ["prevent"])),
                          onDragleave: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (isDragging.value = false), ["prevent"])),
                          onDrop: _withModifiers(handleDrop, ["prevent"])
                        }, [
                          _createElementVNode("input", {
                            ref_key: "fileInputRef",
                            ref: fileInputRef,
                            type: "file",
                            class: "regen-file-input",
                            multiple: "",
                            accept: ".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx",
                            onChange: handleFileChange,
                            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                          }, null, 544),
                          _createElementVNode("div", _hoisted_15, [
                            _createVNode(_unref(UploadCloud), { class: "regen-dropzone-icon" })
                          ]),
                          _cache[20] || (_cache[20] = _createElementVNode("p", { class: "regen-dropzone-heading" }, "Click to select or drag & drop documents", -1)),
                          _cache[21] || (_cache[21] = _createElementVNode("p", { class: "regen-dropzone-hint" }, "PDF, PNG, JPG, CSV, or Text — Max 20MB per file", -1))
                        ], 34)
                      ]))
                    : _createCommentVNode("", true),
                  (!isAutoDerived.value && selectedFiles.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                        _createElementVNode("div", _hoisted_17, [
                          _createElementVNode("span", _hoisted_18, _toDisplayString(selectedFiles.value.length) + " file" + _toDisplayString(selectedFiles.value.length > 1 ? 's' : '') + " selected ", 1),
                          _createElementVNode("button", {
                            type: "button",
                            class: "regen-clear-btn",
                            onClick: _cache[5] || (_cache[5] = ($event: any) => (selectedFiles.value = []))
                          }, " Clear all ")
                        ]),
                        _createElementVNode("div", _hoisted_19, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedFiles.value, (file, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: index,
                              class: "regen-file-item"
                            }, [
                              _createElementVNode("div", _hoisted_20, [
                                _createVNode(_unref(FileText), { class: "regen-file-icon" }),
                                _createElementVNode("span", {
                                  class: "regen-file-name",
                                  title: file.name
                                }, _toDisplayString(file.name), 9, _hoisted_21),
                                _createElementVNode("span", _hoisted_22, _toDisplayString(formatFileSize(file.size)), 1)
                              ]),
                              _createElementVNode("button", {
                                type: "button",
                                class: "regen-file-remove",
                                title: "Remove file",
                                onClick: _withModifiers(($event: any) => (removeFile(index)), ["stop"])
                              }, [
                                _createVNode(_unref(X), { class: "regen-file-remove-icon" })
                              ], 8, _hoisted_23)
                            ]))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_24, [
                    _createElementVNode("label", _hoisted_25, _toDisplayString(isAutoDerived.value ? 'AI Synthesis Model' : 'AI Extraction Model'), 1),
                    _createElementVNode("div", _hoisted_26, [
                      _createVNode(_unref(Sparkles), { class: "regen-model-icon" }),
                      _withDirectives(_createElementVNode("select", {
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((aiModel).value = $event)),
                        class: "regen-model-select"
                      }, [...(_cache[23] || (_cache[23] = [
                        _createElementVNode("option", { value: "gpt-4o-mini" }, "OpenAI GPT-4o Mini — Default (Fast & Accurate)", -1),
                        _createElementVNode("option", { value: "gpt-4o" }, "OpenAI GPT-4o — Deep Analysis (Slower)", -1),
                        _createElementVNode("option", { value: "gpt-4.1" }, "OpenAI GPT-4.1 — Latest High Quality", -1),
                        _createElementVNode("option", { value: "gpt-4.1-mini" }, "OpenAI GPT-4.1 Mini — Fast & Capable", -1),
                        _createElementVNode("option", { value: "gpt-4.1-nano" }, "OpenAI GPT-4.1 Nano — Fastest & Cheapest", -1),
                        _createElementVNode("option", { value: "gpt-5" }, "OpenAI GPT-5 — Flagship Reasoning", -1),
                        _createElementVNode("option", { value: "gpt-5-mini" }, "OpenAI GPT-5 Mini — Fast Reasoning", -1),
                        _createElementVNode("option", { value: "gpt-5-nano" }, "OpenAI GPT-5 Nano — Fastest", -1),
                        _createElementVNode("option", { value: "gpt-4-turbo" }, "OpenAI GPT-4 Turbo", -1),
                        _createElementVNode("option", { value: "gpt-3.5-turbo" }, "OpenAI GPT-3.5 Turbo (Legacy)", -1)
                      ]))], 512), [
                        [_vModelSelect, aiModel.value]
                      ]),
                      _createVNode(_unref(ChevronDown), { class: "regen-model-chevron" })
                    ])
                  ]),
                  (errorMessage.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                        _createVNode(_unref(AlertCircle), { class: "regen-error-icon" }),
                        _createElementVNode("span", null, _toDisplayString(errorMessage.value), 1)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "regen-btn regen-btn--cancel",
                    disabled: isSubmitting.value,
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (isOpen.value = false))
                  }, " Cancel ", 8, _hoisted_29),
                  _createElementVNode("button", {
                    type: "button",
                    class: "regen-btn regen-btn--primary",
                    disabled: (!isAutoDerived.value && selectedFiles.value.length === 0) || isSubmitting.value,
                    onClick: handleRegenerate
                  }, [
                    _createVNode(_unref(RefreshCw), {
                      class: _normalizeClass(["regen-btn-icon", { 'animate-spin': isSubmitting.value }])
                    }, null, 8, ["class"]),
                    _createTextVNode(" " + _toDisplayString(isSubmitting.value ? 'Regenerating…' : (isAutoDerived.value ? 'Regenerate Snapshot' : 'Regenerate Section')), 1)
                  ], 8, _hoisted_30)
                ])
              ], 40, _hoisted_1)
            ]),
            _: 1
          })
        ], 32))
      : _createCommentVNode("", true)
  ]))
}
}

})